<template>
  <section class="terrain-builder" aria-label="Constructor de plantilla de terreno">
    <header class="terrain-builder__header">
      <div>
        <h2>Secciones del formulario</h2>
        <p>Organice las preguntas en el orden en que se responderán en terreno.</p>
      </div>
      <button type="button" class="terrain-builder__primary-action" @click="addSection">
        Agregar sección
      </button>
    </header>

    <p v-if="!definition.sections.length" class="terrain-builder__empty">
      Aún no hay secciones. Cree una para comenzar a agregar preguntas.
    </p>

    <SectionEditor
      v-for="section in definition.sections"
      :key="section.sectionId"
      :section="section"
      :definition="definition"
      :is-first="isFirstSection(section.sectionId)"
      :is-last="isLastSection(section.sectionId)"
      @update="updateSection"
      @duplicate="duplicateSection"
      @remove="removeSection"
      @move="moveSection"
    />
  </section>
</template>

<script>
import SectionEditor from "./SectionEditor.vue";
import { cloneSection, createSection } from "./factories.js";
import { blocksMutation } from "./conditionModel.js";

export { createQuestion, createSection, createStableId } from "./factories.js";

export default {
  name: "TerrainBuilder",
  components: { SectionEditor },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  computed: {
    definition() {
      return {
        schemaVersion: 1,
        title: "",
        description: "",
        reviewSections: [],
        ...this.value,
        sections: this.value.sections || [],
      };
    },
  },
  methods: {
    commitSections(sections) {
      const next = { ...this.definition, sections };
      const errors = blocksMutation(this.definition, next);
      if (errors.length) { window.alert(errors.join("\n")); return false; }
      this.emitDefinition(sections); return true;
    },
    emitDefinition(sections) {
      this.$emit("input", { ...this.definition, sections });
    },
    addSection() {
      this.emitDefinition([...this.definition.sections, createSection()]);
    },
    updateSection(updatedSection) {
      this.commitSections(this.definition.sections.map((section) => (
        section.sectionId === updatedSection.sectionId ? updatedSection : section
      )));
    },
    duplicateSection(sectionId) {
      const source = this.definition.sections.find((section) => section.sectionId === sectionId);
      if (!source) return;
      const duplicate = cloneSection(source);
      const sections = [...this.definition.sections];
      const sourceIndex = sections.findIndex((section) => section.sectionId === sectionId);
      sections.splice(sourceIndex + 1, 0, duplicate);
      this.commitSections(sections);
    },
    removeSection(sectionId) {
      this.commitSections(this.definition.sections.filter((section) => section.sectionId !== sectionId));
    },
    moveSection({ sectionId, direction }) {
      const sections = [...this.definition.sections];
      const currentIndex = sections.findIndex((section) => section.sectionId === sectionId);
      const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= sections.length) return;
      const [section] = sections.splice(currentIndex, 1);
      sections.splice(targetIndex, 0, section);
      this.commitSections(sections);
    },
    isFirstSection(sectionId) {
      return this.definition.sections[0] && this.definition.sections[0].sectionId === sectionId;
    },
    isLastSection(sectionId) {
      const sections = this.definition.sections;
      return sections.length > 0 && sections[sections.length - 1].sectionId === sectionId;
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-builder { display: grid; gap: var(--terrain-space-lg); }

.terrain-builder__header {
  display: flex;
  justify-content: space-between;
  gap: var(--terrain-space-lg);
  align-items: center;
}

.terrain-builder__header h2 { margin: 0; color: var(--color-navy); font-size: var(--terrain-font-heading); }
.terrain-builder__header p { margin: var(--terrain-space-xs) 0 0; color: var(--color-ghost); }

.terrain-builder__primary-action {
  border: 0;
  border-radius: var(--terrain-radius-xl);
  background: var(--color-navy);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  white-space: nowrap;
  transition: background-color var(--terrain-transition);

  &:hover { background: var(--color-steel); }
}

.terrain-builder__empty {
  margin: 0;
  padding: var(--terrain-space-lg);
  border: 1px dashed var(--color-line);
  border-radius: var(--terrain-radius-lg);
  color: var(--color-ghost);
  text-align: center;
}

@media (max-width: 600px) { .terrain-builder__header { align-items: stretch; flex-direction: column; } }
</style>
